import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Copy, ExternalLink, MoreHorizontal, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  createLeadSourcePhrase,
  deleteLeadSourcePhrase,
  listLeadSourcePhrases,
  updateLeadSourcePhrase,
  type LeadSourcePhrase,
} from '@/entities/chats/api/crmSourcesApi';
import { ApiError } from '@/shared/api';
import { labelOf, useI18n, type Messages } from '@/shared/i18n';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { Switch } from '@/shared/ui/shadcn/switch';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { HeaderAction, PageHeader } from '@/widgets/page-header';

const SOURCES = ['quiz', 'alliance', 'marketplace'] as const;
const LANGUAGES = ['ru', 'en'] as const;

type Draft = {
  source: string;
  language: string;
  phrase: string;
  enabled: boolean;
};

const emptyDraft = (): Draft => ({
  source: 'quiz',
  language: 'ru',
  phrase: '',
  enabled: true,
});

function draftFromPhrase(phrase: LeadSourcePhrase): Draft {
  return {
    source: phrase.source,
    language: phrase.language,
    phrase: phrase.phrase,
    enabled: phrase.enabled,
  };
}

function sourceLabel(t: Messages, source: string) {
  return labelOf(t.fields, source) || source;
}

async function copyText(text: string) {
  await navigator.clipboard.writeText(text);
}

function PhraseCard({
  phrase,
  busy,
  onToggle,
  onEdit,
  onDelete,
}: {
  phrase: LeadSourcePhrase;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const link = phrase.wa_me_url?.trim() ?? '';

  const copyLink = async () => {
    if (!link) return;
    try {
      await copyText(link);
      toast.success(t.crm.sourcesLinkCopied);
    } catch {
      toast.error(t.users.copyFailed);
    }
  };

  return (
    <li className="rounded-xl border border-border/80 bg-background p-4 shadow-none">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium tracking-tight">{sourceLabel(t, phrase.source)}</p>
            <Badge variant="outline">{phrase.language.toUpperCase()}</Badge>
            {!phrase.enabled ? (
              <Badge variant="secondary">{t.crm.leadSourceNone}</Badge>
            ) : null}
          </div>
          <p className="text-sm break-words whitespace-pre-wrap text-foreground/90">{phrase.phrase}</p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!link || busy}
              onClick={() => void copyLink()}
            >
              <Copy className="size-3.5" aria-hidden />
              {t.crm.sourcesCopyLink}
            </Button>
            {link ? (
              <Button type="button" size="sm" variant="ghost" render={<a href={link} target="_blank" rel="noreferrer" />}>
                <ExternalLink className="size-3.5" aria-hidden />
                {t.crm.sourcesOpenLink}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Switch
            size="sm"
            checked={phrase.enabled}
            disabled={busy}
            onCheckedChange={(checked) => onToggle(Boolean(checked))}
            aria-label={t.crm.sourcesEnabled}
          />
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  disabled={busy}
                  aria-label={t.crm.sourcesEdit}
                />
              }
            >
              <MoreHorizontal className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>{t.crm.sourcesEdit}</DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={onDelete}>
                {t.crm.sourcesDelete}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </li>
  );
}

function PhraseFormFields({
  draft,
  onChange,
  disabled,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          value={draft.source}
          onValueChange={(value) => value && onChange({ ...draft, source: value })}
          disabled={disabled}
        >
          <SelectTrigger className="w-full" aria-label={t.crm.leadSource}>
            <SelectValue>{(value) => sourceLabel(t, String(value))}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {SOURCES.map((source) => (
              <SelectItem key={source} value={source}>
                {sourceLabel(t, source)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={draft.language}
          onValueChange={(value) => value && onChange({ ...draft, language: value })}
          disabled={disabled}
        >
          <SelectTrigger className="w-full" aria-label={t.crm.sourcesLanguage}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.map((language) => (
              <SelectItem key={language} value={language}>
                {language.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Textarea
        value={draft.phrase}
        onChange={(event) => onChange({ ...draft, phrase: event.target.value })}
        placeholder={t.crm.sourcesPhrasePlaceholder}
        rows={4}
        disabled={disabled}
        aria-label={t.crm.sourcesPhrase}
      />
      <label className="flex items-center gap-2 text-sm">
        <Switch
          size="sm"
          checked={draft.enabled}
          disabled={disabled}
          onCheckedChange={(checked) => onChange({ ...draft, enabled: Boolean(checked) })}
        />
        {t.crm.sourcesEnabled}
      </label>
    </div>
  );
}

export function CrmSourcesPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<Draft>(emptyDraft);
  const [editing, setEditing] = useState<LeadSourcePhrase | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft);
  const [deleting, setDeleting] = useState<LeadSourcePhrase | null>(null);

  const phrasesQuery = useQuery({
    queryKey: ['crm-source-phrases'],
    queryFn: listLeadSourcePhrases,
  });

  const phrases = phrasesQuery.data ?? [];
  const nextSortOrder = useMemo(() => {
    if (phrases.length === 0) return 10;
    return Math.max(...phrases.map((item) => item.sort_order)) + 10;
  }, [phrases]);
  const missingWaMe = phrases.length > 0 && phrases.every((item) => !item.wa_me_url);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['crm-source-phrases'] });
  };

  const create = useMutation({
    mutationFn: (draft: Draft) =>
      createLeadSourcePhrase({
        ...draft,
        sort_order: nextSortOrder,
      }),
    onSuccess: async () => {
      setCreateOpen(false);
      setCreateDraft(emptyDraft());
      toast.success(t.crm.saved);
      await invalidate();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.sourcesCreateFailed);
    },
  });

  const update = useMutation({
    mutationFn: (input: { id: string; draft: Partial<Draft> }) =>
      updateLeadSourcePhrase(input.id, input.draft),
    onSuccess: async () => {
      setEditing(null);
      toast.success(t.crm.saved);
      await invalidate();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.sourcesUpdateFailed);
    },
  });

  const remove = useMutation({
    mutationFn: deleteLeadSourcePhrase,
    onSuccess: async () => {
      setDeleting(null);
      toast.success(t.crm.saved);
      await invalidate();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.sourcesDeleteFailed);
    },
  });

  const busy = create.isPending || update.isPending || remove.isPending;

  const openCreate = () => {
    setCreateDraft(emptyDraft());
    setCreateOpen(true);
  };

  const openEdit = (phrase: LeadSourcePhrase) => {
    setEditing(phrase);
    setEditDraft(draftFromPhrase(phrase));
  };

  return (
    <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden">
      <PageHeader
        title={t.crm.sourcesTitle}
        description={t.crm.sourcesDescription}
        actions={
          <HeaderAction label={t.crm.sourcesAdd} onClick={openCreate} disabled={busy}>
            <Plus className="size-4" />
          </HeaderAction>
        }
      />
      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 p-4">
          {missingWaMe ? (
            <p className="text-sm text-muted-foreground">{t.crm.sourcesWaMeMissing}</p>
          ) : null}

          {phrasesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t.common.loading}</p>
          ) : phrasesQuery.isError ? (
            <p className="text-sm text-destructive">{t.crm.sourcesLoadFailed}</p>
          ) : phrases.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.crm.sourcesEmpty}</p>
          ) : (
            <ul className="space-y-3">
              {phrases.map((phrase) => (
                <PhraseCard
                  key={phrase.id}
                  phrase={phrase}
                  busy={busy}
                  onToggle={(enabled) => update.mutate({ id: phrase.id, draft: { enabled } })}
                  onEdit={() => openEdit(phrase)}
                  onDelete={() => setDeleting(phrase)}
                />
              ))}
            </ul>
          )}
        </div>
      </ScrollArea>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open && !create.isPending) setCreateOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.crm.sourcesCreateTitle}</DialogTitle>
            <DialogDescription>{t.crm.sourcesCreateHint}</DialogDescription>
          </DialogHeader>
          <PhraseFormFields draft={createDraft} onChange={setCreateDraft} disabled={create.isPending} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={create.isPending}
              onClick={() => setCreateOpen(false)}
            >
              {t.crm.cancel}
            </Button>
            <Button
              type="button"
              disabled={create.isPending || !createDraft.phrase.trim()}
              onClick={() => create.mutate(createDraft)}
            >
              {create.isPending ? t.common.creating : t.crm.sourcesAdd}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(open) => {
          if (!open && !update.isPending) setEditing(null);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.crm.sourcesEditTitle}</DialogTitle>
            <DialogDescription>{t.crm.sourcesCreateHint}</DialogDescription>
          </DialogHeader>
          <PhraseFormFields draft={editDraft} onChange={setEditDraft} disabled={update.isPending} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={update.isPending}
              onClick={() => setEditing(null)}
            >
              {t.crm.cancel}
            </Button>
            <Button
              type="button"
              disabled={update.isPending || !editDraft.phrase.trim() || !editing}
              onClick={() => {
                if (!editing) return;
                update.mutate({ id: editing.id, draft: editDraft });
              }}
            >
              {update.isPending ? t.common.saving : t.crm.sourcesSave}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setDeleting(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.crm.sourcesDeleteConfirm}</DialogTitle>
            <DialogDescription>{t.crm.sourcesDeleteConfirmHint}</DialogDescription>
          </DialogHeader>
          {deleting ? (
            <p className="text-sm break-words whitespace-pre-wrap text-muted-foreground">
              {sourceLabel(t, deleting.source)} · {deleting.language.toUpperCase()}
              {'\n'}
              {deleting.phrase}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={remove.isPending}
              onClick={() => setDeleting(null)}
            >
              {t.crm.cancel}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={remove.isPending || !deleting}
              onClick={() => {
                if (!deleting) return;
                remove.mutate(deleting.id);
              }}
            >
              {remove.isPending ? t.common.saving : t.crm.sourcesDelete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
